// src/components/StaffAbsences/StaffAbsencesCalendar.jsx
import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import bootstrapPlugin from "@fullcalendar/bootstrap";
import { Icon } from "../../components";
import { useApiClient } from "../../ApiService/useApiClient";
import { staffAbsencesApi } from "../../ApiService/staffAbsencesApi";
import { AppContext } from "../../context/AppContext";

const safeArr = (v) => (Array.isArray(v) ? v : []);

const normalize = (a) => {
  if (!a) return null;
  return {
    id: a.id ?? a.Id,
    agentId: a.agentId ?? a.AgentId,
    agentName: a.agentName ?? a.AgentName ?? a.name ?? a.Name ?? "",
    type: String(a.type ?? a.Type ?? "").toLowerCase(), // "holiday" | "offsick"
    status: String(a.status ?? a.Status ?? "").toLowerCase(), // "pending" | "approved" | "cancelled"
    startDate: a.startDate ?? a.StartDate,
    endDate: a.endDate ?? a.EndDate,
    isAllDay: a.isAllDay ?? a.IsAllDay ?? true,
    reason: a.reason ?? a.Reason ?? "",
  };
};

const badgeForStatus = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("approved")) return { bg: "success", text: "Approved" };
  if (s.includes("cancel")) return { bg: "secondary", text: "Cancelled" };
  if (s.includes("pending")) return { bg: "warning", text: "Pending" };
  return { bg: "primary", text: status || "Unknown" };
};

const badgeForType = (type) => {
  const t = String(type || "").toLowerCase();
  if (t.includes("holiday")) return { bg: "info", text: "Holiday" };
  return { bg: "danger", text: "Off sick" };
};

// FullCalendar expects end to be exclusive for all-day ranges.
// If your backend stores EndDate as inclusive, we add +1 day for all-day events.
const toExclusiveEnd = (endIso, isAllDay) => {
  if (!endIso) return endIso;
  if (!isAllDay) return endIso;
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime())) return endIso;
  end.setUTCDate(end.getUTCDate() + 1);
  return end.toISOString();
};

// Formats Date for display in modal
const fmtDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toISOString().slice(0, 10);
};

export default function StaffAbsencesCalendar() {
  const api = useApiClient();
  const apiWrap = useMemo(() => staffAbsencesApi(api), [api]);
  const { SiteConfig } = useContext(AppContext);

  const isAdmin = SiteConfig?.CURRENT_USER_ROLE === "admin";

  const calendarRef = useRef(null);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters match StaffAbsencesQueryDto
  const [typeFilter, setTypeFilter] = useState("all"); // all | holiday | offsick
  const [statusFilter, setStatusFilter] = useState("all"); // all | pending | approved | cancelled
  const [agentId, setAgentId] = useState(""); // optional
  const [search, setSearch] = useState("");

  const [error, setError] = useState(null);

  // modal
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const closeModal = () => setShowModal(false);

  /**
   * Calls GET StaffAbsences/dash?From=...&To=...&Type=...&Status=...&AgentId=...&Search=...
   * Make sure your staffAbsencesApi.getRange uses api.get("StaffAbsences/dash", paramsObj)
   */
  const fetchRange = useCallback(
    async ({ startStr, endStr }) => {
      if (!isAdmin) return;

      setLoading(true);
      setError(null);

      try {
        // Build query matching DTO property names (From/To/Type/Status/AgentId/Search)
        const q = {
          From: startStr,
          To: endStr,
          Type: typeFilter,
          Status: statusFilter,
          // Only send AgentId if numeric
          AgentId: agentId ? Number(agentId) : undefined,
          Search: search?.trim() ? search.trim() : undefined,
        };

        // ✅ IMPORTANT: your wrapper should NOT nest params again.
        // staffAbsencesApi.getRange should call api.get("StaffAbsences/dash", q)
        const res = await apiWrap.getRange(q);

        const items = safeArr(res.data).map(normalize).filter(Boolean);

        // Map to FullCalendar events
        const fcEvents = items.map((a) => {
          const tb = badgeForType(a.type);
          const sb = badgeForStatus(a.status);

          const title = `${a.agentName || "Staff"} • ${tb.text}${sb.text === "Pending" ? " (Pending)" : ""}`;

          return {
            id: String(a.id),
            title,
            start: a.startDate,
            end: toExclusiveEnd(a.endDate, a.isAllDay),
            allDay: !!a.isAllDay,
            extendedProps: a,
            classNames: [
              a.type === "holiday" ? "event-info" : "event-danger",
              a.status === "pending" ? "event-warning" : "",
              a.status === "cancelled" ? "event-secondary" : "",
            ].filter(Boolean),
          };
        });

        setEvents(fcEvents);
      } catch (e) {
        setError(e);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    },
    [apiWrap, isAdmin, typeFilter, statusFilter, agentId, search]
  );

  const onDatesSet = useCallback(
    (arg) => {
      fetchRange({ startStr: arg.startStr, endStr: arg.endStr });
    },
    [fetchRange]
  );

  const onEventClick = useCallback((clickInfo) => {
    const a = clickInfo.event?.extendedProps;
    setSelected(a || null);
    setShowModal(true);
  }, []);

  const refreshCurrentRange = useCallback(() => {
    const cal = calendarRef.current?.getApi?.();
    if (!cal) return;
    const view = cal.view;
    fetchRange({ startStr: view.activeStart.toISOString(), endStr: view.activeEnd.toISOString() });
  }, [fetchRange]);

  const approve = async () => {
    if (!selected?.id) return;
    try {
      setLoading(true);
      await apiWrap.approve(selected.id);
      setShowModal(false);
      refreshCurrentRange();
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!selected?.id) return;
    try {
      setLoading(true);
      await apiWrap.cancel(selected.id);
      setShowModal(false);
      refreshCurrentRange();
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  const sb = badgeForStatus(selected?.status);
  const tb = badgeForType(selected?.type);

  if (!isAdmin) {
    return <Alert variant="warning">You don’t have permission to view the staff absences calendar.</Alert>;
  }

  return (
    <>
      <Card>
        <Card.Body>
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
            <div>
              <div className="fw-bold">Staff Absences Calendar</div>
              <div className="small text-muted">View holidays and off sick periods across all staff.</div>
            </div>

            <div className="d-flex gap-2 align-items-center">
              <Button variant="outline-secondary" onClick={refreshCurrentRange} disabled={loading}>
                <Icon name="refresh" /> <span>Refresh</span>
              </Button>
            </div>
          </div>

          {/* Filters (match StaffAbsencesQueryDto) */}
          <Row className="g-2 mb-3">
            <Col xs={12} md={3}>
              <Form.Label className="small text-muted mb-1">Type</Form.Label>
              <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="all">All types</option>
                <option value="holiday">Holiday</option>
                <option value="offsick">Off sick</option>
              </Form.Select>
            </Col>

            <Col xs={12} md={3}>
              <Form.Label className="small text-muted mb-1">Status</Form.Label>
              <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </Col>

            <Col xs={12} md={2}>
              <Form.Label className="small text-muted mb-1">Agent ID (optional)</Form.Label>
              <Form.Control
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                placeholder="e.g. 101"
                inputMode="numeric"
              />
            </Col>

            <Col xs={12} md={3}>
              <Form.Label className="small text-muted mb-1">Search</Form.Label>
              <Form.Control value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff name..." />
            </Col>

            <Col xs={12} md={1} className="d-flex align-items-end">
              <Button className="w-100" variant="primary" onClick={refreshCurrentRange} disabled={loading}>
                Apply
              </Button>
            </Col>
          </Row>

          {error ? (
            <Alert variant="danger">
              {String(error?.response?.data?.message || error?.message || error)}
            </Alert>
          ) : null}

          {loading ? (
            <div className="d-flex align-items-center gap-2 mb-2">
              <Spinner animation="border" size="sm" />
              <div className="small text-muted">Loading absences…</div>
            </div>
          ) : null}

          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, bootstrapPlugin]}
            initialView="dayGridMonth"
            themeSystem="bootstrap"
            timeZone="UTC"
            headerToolbar={{
              left: "title prev,next today",
              center: "",
              right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
            }}
            height={820}
            events={events}
            datesSet={onDatesSet}
            eventClick={onEventClick}
            eventDisplay="block"
            dayMaxEventRows={4}
            noEventsText="No absences found in this range"
          />
        </Card.Body>
      </Card>

      {/* Details modal */}
      <Modal show={showModal} onHide={closeModal} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Absence details</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selected ? (
            <>
              <div className="d-flex gap-2 mb-2">
                <Badge bg={tb.bg}>{tb.text}</Badge>
                <Badge bg={sb.bg}>{sb.text}</Badge>
              </div>

              <div className="fw-bold">{selected.agentName || "Staff"}</div>

              <div className="small text-muted mt-1">
                {fmtDate(selected.startDate)} → {fmtDate(selected.endDate)}
                {selected.isAllDay ? " • all-day" : ""}
              </div>

              <div className="mt-3">
                <div className="small text-muted">Reason</div>
                <div>{selected.reason || "-"}</div>
              </div>
            </>
          ) : (
            <div className="small text-muted">No selection</div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" onClick={closeModal}>
            Close
          </Button>

          {/* Admin workflow */}
          {selected && !String(selected.status || "").includes("cancel") ? (
            <>
              {!String(selected.status || "").includes("approved") ? (
                <Button variant="success" onClick={approve} disabled={loading}>
                  <Icon name="check" /> <span>Approve</span>
                </Button>
              ) : null}

              <Button variant="outline-danger" onClick={cancel} disabled={loading}>
                <Icon name="cross" /> <span>Cancel</span>
              </Button>
            </>
          ) : null}
        </Modal.Footer>
      </Modal>
    </>
  );
}
